import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import { flexRender } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  TableSkeletonRows,
  TableStateRow,
} from "@/components/ui/states";

export interface ResourceTableProps<TData extends { id: string }> {
  /** Refine resource name (kebab path), e.g. "skills". */
  resource: string;
  /** Page title, rendered through the shared PageHeader. */
  title: string;
  /** One-line subtitle under the title (expected on every page). */
  subtitle?: string;
  /** TanStack column definitions. */
  columns: ColumnDef<TData>[];
  /** Default sort field/order applied on first load. */
  initialSort?: { field: string; order: "asc" | "desc" };
  /** Header action content (e.g. Create / custom-view buttons). */
  toolbar?: ReactNode;
  /** Empty-state message. */
  emptyMessage?: string;
}

/**
 * Reusable server-driven list page for the Operate/execution family
 * (skills / processes / phases / rules). Renders the shared `PageHeader`
 * (title + subtitle + actions) over a single hairline-bordered table built from
 * shadcn primitives, with the canonical loading / empty states. The whole row is
 * clickable through the `actions` column's `View`; sorting + pagination are
 * server-driven via `@refinedev/react-table`.
 */
export function ResourceTable<TData extends { id: string }>({
  resource,
  title,
  subtitle,
  columns,
  initialSort,
  toolbar,
  emptyMessage = "No records yet.",
}: ResourceTableProps<TData>) {
  const {
    getHeaderGroups,
    getRowModel,
    refineCore: { tableQuery },
    getState,
    setPageIndex,
    getCanPreviousPage,
    getCanNextPage,
    nextPage,
    previousPage,
    getPageCount,
  } = useTable<TData>({
    columns,
    refineCoreProps: {
      resource,
      pagination: { pageSize: 25 },
      sorters: initialSort
        ? { initial: [{ field: initialSort.field, order: initialSort.order }] }
        : undefined,
    },
  });

  const isLoading = tableQuery.isLoading;
  const rows = getRowModel().rows;
  const pageIndex = getState().pagination.pageIndex;
  const pageCount = getPageCount();

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={toolbar ? <div className="flex gap-2">{toolbar}</div> : undefined}
      />

      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            {getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className={canSort ? "cursor-pointer select-none" : ""}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {canSort && sorted === "asc" ? (
                          <Icon name="sortAsc" size={12} aria-hidden />
                        ) : canSort && sorted === "desc" ? (
                          <Icon name="sortDesc" size={12} aria-hidden />
                        ) : null}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={columns.length} />
            ) : rows.length === 0 ? (
              <TableStateRow columns={columns.length}>
                <EmptyState message={emptyMessage} />
              </TableStateRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          Page {pageIndex + 1} of {Math.max(pageCount, 1)}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageIndex(0)}
            disabled={!getCanPreviousPage()}
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => previousPage()}
            disabled={!getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => nextPage()}
            disabled={!getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ResourceTable;
